import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buscarVeiculosRastreamento,
  type VeiculoRastreado,
} from '../../../api/rastreamentoApi';
import CarClassIcon from '../../../components/car-class-icon/CarClassIcon';
import RastreamentoVeiculoModal from '../../../components/modal/rastreamento-veiculo/RastreamentoVeiculoModal';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import Paginacao from '../../../components/paginacao/Paginacao';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { notify } from '../../../shared/feedback/notify';
import SearchIcon from '../../../img/search-icon.svg';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto, tabela em grid, controles e paginação são as classes compartilhadas do sistema
// (`ConteDo`, `recepcao-*`, `VisualizarOrdenar`, `CtaBig`, `TabelaScroll`, `Tabela`...).
// Import explícito: contar com o CSS carregado por outra tela é acoplamento invisível
// (lição do BUG-V74/at-67).
import '../../recepcao/pages/Recepcao.css';
import './RastreamentoPage.css';

/**
 * Sem props. A tela recebia `sidebarRef` do `App.tsx` e o REATRIBUÍA ao próprio container —
 * o que sequestrava o ref da sidebar e quebrava o fechamento por clique fora do modal da
 * at-76 (BUG-R27). Como nada mais aqui precisa dele, a prop deixou de existir em vez de
 * ficar aceita e ignorada.
 */

const headers = ['Placa', 'Modelo', 'Classe', 'Associado', 'Telefone', 'Na oficina desde'];
const defaultColumnWidths = [110, 180, 140, 170, 140, 150];

const dataCurta = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

/** Telefone só com dígitos vindo do banco — exibido legível, sem inventar DDI. */
const telefoneLegivel = (valor: string | null): string => {
  if (!valor) return '-';
  const d = valor.replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return valor;
};

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela mostra',
    conteudo: (
      <>
        Os veículos que estão <strong>em atendimento na oficina agora</strong>, de todas as
        classes (associado e terceiro), com os dados de contato do proprietário. Se o veículo
        não aparece na lista, ele não está na oficina.
      </>
    ),
  },
  {
    titulo: 'Buscar por placa antes de ligar',
    conteudo: (
      <>
        Digite a placa no campo de busca para ter a resposta direta: se o veículo está na
        oficina (e desde quando), se <strong>saiu recentemente</strong> — atendimentos
        encerrados nos últimos 30 dias aparecem com a data de saída — ou se não está aqui. A
        busca aceita a placa parcial e não diferencia maiúsculas nem hífen.
      </>
    ),
  },
  {
    titulo: 'Quando a consulta falha',
    conteudo: (
      <>
        Se a consulta não puder ser feita, a tela avisa com uma faixa vermelha e{' '}
        <strong>não</strong> diz que o veículo está fora da oficina — porque nesse caso o sistema
        não conseguiu verificar. Só trate a ausência como resposta quando não houver aviso de
        erro na tela.
      </>
    ),
  },
  {
    titulo: 'Consultar a posição no rastreador',
    conteudo: (
      <>
        Clique na <strong>placa</strong> para abrir o veículo. Dentro, o botão{' '}
        <strong>Consultar posição agora</strong> pergunta à rastreadora onde o veículo está e
        mostra ignição, movimento, velocidade e o link do mapa. A consulta{' '}
        <strong>fica registrada</strong> com o seu nome e a data, e substitui a anterior — por
        isso ela é uma ação sua, e não algo que acontece só por abrir a tela.
      </>
    ),
  },
  {
    titulo: 'Quando a rastreadora diz que não localizou o veículo',
    conteudo: (
      <>
        A rastreadora só informa a posição de um veículo para o <strong>cliente do contrato</strong>{' '}
        de rastreamento — e esse cliente <strong>nem sempre é o mesmo</strong>: em alguns veículos
        é a empresa, em outros é o próprio associado. A consulta tenta sozinha os documentos que o
        sistema conhece: o que já estiver gravado neste atendimento, o da empresa e, quando o
        evento é reconhecido pelo SGA, o do associado.
        <br />
        <br />
        Falhando todos, a tela mostra <strong>quais foram tentados</strong> e libera o campo{' '}
        <strong>CPF/CNPJ do contrato</strong>, no rodapé do painel de posição. Esse número
        aparece no painel da rastreadora, em <strong>Dados do cliente</strong>. Depois de
        informá-lo, clique novamente em <strong>Consultar posição agora</strong>: dando certo, o
        documento fica guardado e as próximas consultas daquele veículo já saem diretas.
        <br />
        <br />
        Vale insistir num ponto: esse documento é de <strong>quem contratou o rastreamento</strong>,
        que pode não ser o proprietário do veículo. Toda alteração fica registrada com o seu nome
        e a data.
      </>
    ),
  },
  {
    titulo: 'O IMEI do rastreador, e por que quase nunca você precisa dele',
    conteudo: (
      <>
        O <strong>IMEI</strong> é o número de série do rastreador instalado no veículo, e é por
        ele que a rastreadora identifica o equipamento. Você <strong>não precisa</strong>{' '}
        cadastrá-lo antes de consultar: quando o veículo ainda não tem IMEI, a consulta é feita{' '}
        <strong>pela placa</strong> e, dando certo, o número é gravado sozinho no cadastro — o
        aviso na tela conta quando isso acontece, e a gravação fica registrada com o seu nome.
        <br />
        <br />
        Por isso o IMEI aparece só como uma linha no <strong>rodapé</strong> do painel de
        posição, com a opção de cadastrar ou alterar. Ela resolve os dois casos em que a placa
        não basta: quando a rastreadora <strong>não encontra o veículo pela placa</strong> (a
        placa do cadastro dela pode estar diferente da nossa) e quando o rastreador do veículo{' '}
        <strong>foi trocado</strong>. Toda alteração fica registrada na auditoria com o seu nome,
        a data e o valor anterior.
      </>
    ),
  },
  {
    titulo: 'Quando a placa da rastreadora não confere',
    conteudo: (
      <>
        Toda posição vem acompanhada da placa que a <strong>rastreadora</strong> tem para aquele
        equipamento. Quando ela não é a do veículo que você abriu, aparece um{' '}
        <strong>aviso em destaque</strong> antes da posição — e ele deve ser levado a sério:
        quase sempre significa que o IMEI cadastrado aqui é o de <strong>outro veículo</strong>,
        e a localização na tela é a desse outro carro. Nesse caso, corrija o IMEI antes de
        informar a posição a alguém. A posição continua visível de propósito, para você poder
        conferir do que se trata.
      </>
    ),
  },
  {
    titulo: 'A data da consulta é parte da resposta',
    conteudo: (
      <>
        O veículo abre mostrando a <strong>última consulta já feita</strong>, com a data e quem a
        fez — pode ser de dias atrás. Antes de usar a posição como resposta, confira essa data:
        se não for recente o bastante para o que você precisa responder, consulte de novo. O{' '}
        <strong>Ver no mapa</strong> abre o Google Maps no ponto da consulta — é uma fotografia
        daquele momento, não acompanhamento ao vivo.
      </>
    ),
  },
  {
    titulo: 'O que esta tela não mostra',
    conteudo: (
      <>
        Etapa do serviço, previsão de entrega e a situação interna do atendimento não aparecem
        aqui, de propósito. Precisando desses detalhes, acione a equipe da oficina.
      </>
    ),
  },
];

/**
 * Tela única do papel RASTREADOR (at-75, ADR 0033).
 *
 * ⚠️ A regra que governa esta tela inteira: **"não está na oficina" é uma conclusão, e só pode
 * ser exibida quando a consulta RESPONDEU** (at-60). Lista vazia por 403/500 faria o rastreador
 * ligar para o associado achando que confirmou o contrário — a dor original, agora causada pelo
 * sistema. Por isso `erro` e `respondeu` são estados distintos de "lista vazia".
 */
const RastreamentoPage: React.FC = () => {
  const [termo, setTermo] = useState('');
  /** Termo efetivamente consultado — é dele que a resposta da busca fala, não do que se digita. */
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [itens, setItens] = useState<VeiculoRastreado[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** `false` enquanto a consulta atual não tiver respondido com sucesso. */
  const [respondeu, setRespondeu] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  /** Veículo do modal. `null` = fechado. A tabela é a única porta de entrada dele. */
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<VeiculoRastreado | null>(null);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const consultar = useCallback(async (busca: string, page: number, limit: number) => {
    setCarregando(true);
    try {
      const resposta = await buscarVeiculosRastreamento({ busca, page, limit });
      setItens(resposta.itens ?? []);
      setTotal(resposta.total ?? 0);
      setErro(null);
      setRespondeu(true);
    } catch {
      // Estado seguro E sinal visível: a lista esvazia, mas `respondeu` fica false, então a
      // tela NÃO afirma que o veículo está fora da oficina.
      setItens([]);
      setTotal(0);
      setRespondeu(false);
      setErro(
        'Não foi possível consultar os veículos da oficina. Enquanto este aviso estiver na tela, ' +
          'a lista não serve como resposta — recarregue a página e, se persistir, acione a TI.',
      );
      // TEXTO FIXO, não `errorFrom` (regra da at-65): este aviso é sobre o ESTADO ENGANOSO
      // DA TELA, não sobre uma ação que falhou. `errorFrom` prioriza a mensagem do backend —
      // num 403 ela vira "Você não tem permissão para executar esta ação", que substitui a
      // única informação que importa aqui: a lista na tela não é resposta.
      notify.error(
        'Não foi possível consultar os veículos da oficina. A lista pode estar incompleta — ' +
          'não conclua que um veículo está fora da oficina enquanto este aviso aparecer.',
        { toastId: 'rastreamento-consulta-falhou' },
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void consultar(buscaAplicada, pagina, limite);
  }, [consultar, buscaAplicada, pagina, limite]);

  const aplicarBusca = () => {
    setPagina(1);
    setBuscaAplicada(termo.trim());
  };

  const limparBusca = () => {
    setTermo('');
    setPagina(1);
    setBuscaAplicada('');
  };

  const linhasRenderizadas = useMemo(
    () =>
      itens.map((v) => [
        v.plate || '-',
        v.model || '-',
        v.carClass || '-',
        v.ownerName || '-',
        telefoneLegivel(v.ownerPhone),
        dataCurta(v.entryDate),
      ]),
    [itens],
  );

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: linhasRenderizadas,
  });

  const totalPaginas = Math.max(1, Math.ceil(total / limite));
  const primeiroDaPagina = total === 0 ? 0 : (pagina - 1) * limite + 1;
  const ultimoDaPagina = Math.min(pagina * limite, total);

  /**
   * A resposta em destaque da busca. Só existe quando a consulta respondeu — é a metade da
   * regra da at-60 que impede o falso negativo.
   */
  const respostaDaBusca = (): React.ReactNode => {
    if (!buscaAplicada || !respondeu || carregando) return null;

    if (itens.length === 0) {
      return (
        <div className="rastreamento-resposta rastreamento-resposta--ausente" role="status">
          <strong>{buscaAplicada}</strong> não está na oficina e não saiu nos últimos 30 dias.
        </div>
      );
    }

    const naOficina = itens.filter((v) => !v.entregue);
    if (naOficina.length > 0) {
      const v = naOficina[0];
      return (
        <div className="rastreamento-resposta rastreamento-resposta--presente" role="status">
          <strong>{v.plate}</strong> está na oficina desde {dataCurta(v.entryDate)}.
        </div>
      );
    }

    const v = itens[0];
    return (
      <div className="rastreamento-resposta rastreamento-resposta--saiu" role="status">
        <strong>{v.plate}</strong> não está mais na oficina — saiu em {dataCurta(v.saiuEm)}.
      </div>
    );
  };

  const textoDoContador = (): string => {
    if (carregando) return 'Consultando...';
    if (!respondeu) return 'Não foi possível consultar';
    if (total === 0) return '0 veículos';
    return `${primeiroDaPagina}-${ultimoDaPagina} de ${total} veículos`;
  };

  // ⚠️ NÃO reatribuir `sidebarRef` ao container desta página (era o que a at-75 fazia): o ref
  // é criado no `App.tsx` e já aponta para a SIDEBAR. Um ref object guarda um elemento só, então
  // reatribuí-lo aqui fazia `sidebarRef.current` virar o conteúdo da página inteira — e todo
  // handler de "clicou fora" que o consulta passava a enxergar qualquer clique como clique
  // DENTRO da sidebar. Foi o que impediu o modal da at-76 de fechar (BUG-R27).
  return (
    <div className="ConteDo recepcao-conte-do">
      {/* at-65: a faixa fica DENTRO do conteúdo; substituir a tela apagaria busca e tabela. */}
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Veículos na Oficina
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
        <div className="recepcao-search-bar">
          <div className="ItemSearch" onClick={aplicarBusca}>
            <img className="imgIcon" src={SearchIcon} alt="Ícone de busca" width="20" height="20" />
          </div>
          <input
            type="text"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && aplicarBusca()}
            placeholder="Busque pela placa do veículo"
            aria-label="Buscar placa"
            className="recepcao-search-input"
          />
        </div>
      </div>

      {respostaDaBusca()}

      <div className="VisualizarOrdenar">
        <div
          className="Frame1000004037"
          style={{
            alignSelf: 'stretch',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 12,
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          <div className="CtaBig">
            <div className="Frame1000003987">
              <div className="ComponentName">Itens por página:</div>
              <select
                id="limite"
                value={limite}
                onChange={(e) => {
                  setLimite(Number(e.target.value));
                  setPagina(1);
                }}
                className="ComponentNameImgIcon"
                style={{ cursor: 'pointer' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {buscaAplicada && (
            <div className="CtaBig MouseHover" onClick={limparBusca}>
              <div className="Frame1000003987">
                <div className="ComponentName">Limpar busca</div>
              </div>
            </div>
          )}
        </div>

        <div className="QuantidadeDeCarros">
          <div
            className="Frame1000004037"
            style={{
              alignSelf: 'stretch',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 12,
              display: 'flex',
            }}
          >
            <div className="CtaQuantidadeDeCarros">
              <div className="Frame1000003987">
                <div className="ComponentName">{textoDoContador()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          marginBottom: 20,
        }}
      >
        <div className="TabelaScroll" ref={tableScrollRef}>
          <div
            className="TabelaContent"
            style={{
              ['--table-columns' as any]: tableColumns,
              width: tableWidth,
              minWidth: tableWidth,
            }}
          >
            <div className="Tabela TabelaHeader">
              <div className="Placa">
                <div className="TTulo">
                  {headers.map((header, index) => (
                    <div
                      key={header}
                      className="Opcoes"
                      ref={(el) => {
                        headerRefs.current[index] = el;
                      }}
                    >
                      {header}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {carregando && (
              <div className="Tabela">
                <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                  <div className="Data">Consultando...</div>
                </div>
              </div>
            )}

            {/* "Nenhum veículo" é afirmação de negócio: só quando a consulta respondeu. */}
            {!carregando && itens.length === 0 && respondeu && (
              <div className="Tabela">
                <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                  <div className="Data">
                    {buscaAplicada
                      ? 'Nenhum veículo encontrado para esta placa.'
                      : 'Nenhum veículo em atendimento na oficina no momento.'}
                  </div>
                </div>
              </div>
            )}

            {!carregando && itens.length === 0 && !respondeu && (
              <div className="Tabela">
                <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                  <div className="Data">A consulta não pôde ser feita — veja o aviso acima.</div>
                </div>
              </div>
            )}

            {!carregando &&
              itens.map((v) => (
                <div key={v.id} className="Tabela">
                  <div className="Item">
                    <div className="Data" title={v.plate}>
                      {/* A placa é a porta do modal (at-76). Botão, e não div com onClick: o
                          rastreador precisa alcançá-la por teclado, e o leitor de tela precisa
                          anunciar que ali existe uma ação. */}
                      <button
                        type="button"
                        className="rastreamento-placa-botao"
                        onClick={() => setVeiculoSelecionado(v)}
                      >
                        {v.plate || '-'}
                      </button>
                      {v.entregue && (
                        <span className="rastreamento-selo-saiu">saiu em {dataCurta(v.saiuEm)}</span>
                      )}
                    </div>
                  </div>

                  <div className="Item">
                    <div className="Data" title={v.model ?? ''}>
                      {v.model || '-'}
                    </div>
                  </div>

                  <div className="Item">
                    <div
                      className="Data"
                      title={v.carClass ?? ''}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <CarClassIcon classe={v.carClass} />
                      {v.carClass || '-'}
                    </div>
                  </div>

                  <div className="Item">
                    <div className="Data" title={v.ownerName ?? ''}>
                      {v.ownerName || '-'}
                    </div>
                  </div>

                  <div className="Item">
                    <div className="Data" title={v.ownerPhone ?? ''}>
                      {telefoneLegivel(v.ownerPhone)}
                    </div>
                  </div>

                  <div className="Item">
                    <div className="Data">{dataCurta(v.entryDate)}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {total > limite && (
          <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
        )}
      </div>

      {veiculoSelecionado && (
        <RastreamentoVeiculoModal
          veiculo={veiculoSelecionado}
          onClose={() => setVeiculoSelecionado(null)}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Veículos na Oficina"
        secoes={helpSections}
      />
    </div>
  );
};

export default RastreamentoPage;
